import { DateTime } from '@ksyos/date-time';
import {
    PageHeader,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableHeader,
    TableRow,
} from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { LabResult } from '../../../interfaces/labresult';
import { tryParseInt } from '../../../../../common/utils/tryParseInt';
import { AbortableFetch } from '../../../../store/fetch';
import { translateKey } from '../../../../translate';
import {} from '../../../elements/common';
import { ListPager } from '../../../elements/ListPager';
import { NullableFormattedDate } from '../../../elements/NullableFormattedDate';

interface ILabResultsListProps {
    page?: string;
    patientId: string;
}

export function LabResultsList({ page, patientId }: ILabResultsListProps) {
    const navigate = useNavigate();
    const abortableFetch = new AbortableFetch();
    const itemsPerPage = 10;

    const [total, setTotal] = useState(0);
    const [labResults, setLabresults] = useState<LabResult[] | undefined>();

    useEffect(() => {
        loadLabResults();
    }, [page, patientId]);

    if (!labResults) {
        return null;
    }

    return (
        <>
            <PageHeader
                subtitle={`${translateKey('common.requestedOn')} ${getRequestedOnDate()}`}
                title={translateKey('labResults.overview')}
            />
            <TableContainer>
                <Table spacing="medium">
                    <TableHead>
                        <TableRow>
                            <TableHeader>{translateKey('common.date')}</TableHeader>
                            <TableHeader>{translateKey('labResults.description')}</TableHeader>
                            <TableHeader>{translateKey('labResults.valueValue')}</TableHeader>
                            <TableHeader>{translateKey('labResults.valueUnit')}</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {labResults.map((item) => (
                            <TableRow key={item.id}>
                                <TableCell>
                                    <NullableFormattedDate value={item.startDate} />
                                </TableCell>
                                <TableCell>{item.codeDisplayName}</TableCell>
                                <TableCell>{item.valueValue}</TableCell>
                                <TableCell>{item.valueUnit}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ListPager
                itemsPerPage={itemsPerPage}
                onChangePage={handleNavigation}
                page={tryParseInt(page, 1)}
                total={total}
            />
        </>
    );

    async function handleNavigation(navigateToPage: number) {
        navigate('/patients/' + patientId + '?overview=labResults' + '&page=' + navigateToPage);
    }

    function getRequestedOnDate() {
        if (labResults && labResults.length > 0) {
            return DateTime.from(labResults[0].created_at).format('DD-MM-YYYY');
        } else {
            return '-';
        }
    }

    async function loadLabResults() {
        let url = '/api/patients/' + patientId + '/labResults';
        url += `?limit=${itemsPerPage}${page ? `&page=${page}` : ''}`;

        const result = await abortableFetch.fetchJson(url, 'GET');
        setLabresults(result.json.items);
        setTotal(result.json.total);
    }
}
